import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import BenoLogo from "./BenoLogo";

const navItems = [
  { to: "/", label: "Atlas", testid: "liquid-nav-atlas", end: true },
  { to: "/gin", label: "Gin Atlas", testid: "liquid-nav-gin", end: false },
  { to: "/spirits", label: "Spirits", testid: "liquid-nav-spirits", end: false },
];

export default function LiquidLayout({ children }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div className="App min-h-screen relative">
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-black/60 border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="liquid-nav-logo">
            <BenoLogo size={36} />
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.4em] text-[#C17F59] border-l border-[#C17F59]/40 pl-3">
              Liquid&nbsp;Atlas
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `text-xs uppercase tracking-[0.25em] px-4 py-2 transition-colors ${
                    isActive ? "text-[#C17F59]" : "text-white/70 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <button
            className="md:hidden text-white/80 hover:text-[#C17F59] transition-colors"
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
            data-testid="liquid-mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-white/5 bg-black/85 backdrop-blur-xl">
            <div className="px-5 py-4 grid gap-1">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`${n.testid}-mobile`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 text-sm uppercase tracking-[0.25em] ${
                      isActive ? "text-[#C17F59]" : "text-white/80"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="pt-20 md:pt-24">{children}</main>

      <footer className="border-t border-white/5 mt-24 px-6 md:px-12 py-12 text-center text-xs text-white/40 tracking-[0.3em] uppercase">
        <p>BENO · Liquid Atlas · A hospitality intelligence platform</p>
        <Link
          to="/admin/login"
          className="inline-block mt-4 text-[9px] tracking-[0.4em] text-white/20 hover:text-[#C17F59] transition-colors"
          data-testid="liquid-footer-control-room"
          title="Staff only"
        >
          · BENO Control Room ·
        </Link>
      </footer>
    </div>
  );
}
